import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  updateUserPromise,
  type GetManagementUserSchema,
  LOG_ACTION_TYPE_MAP,
  type CreateUserSchema,
} from 'src/utils/api';
import { useLogAction } from 'src/hooks/api/use-log-action';
import { useNotification } from 'src/hooks/use-notification';
import { QUERY_KEY_MAP } from 'src/constants';

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { logUserAction } = useLogAction();

  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: updateUserPromise,
  });

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const updateNewUser = async (user: Partial<GetManagementUserSchema>, updatedFields?: Array<string>) => {
    try {
      const res = await mutateAsync({
        ...(user || {}),
        ...(user.stores ? { stores: user.stores.map((store) => store.uuid) } : {}),
        ...(user.orgs ? { orgs: user.orgs.map((org) => org.uuid) } : {}),
        ...(user.role ? { role: user.role.map((role) => role.user_role) } : {}),
      } as CreateUserSchema);

      if (res) {
        await logUserAction({
          action: 'update',
          action_from: '',
          action_to: '',
          type: LOG_ACTION_TYPE_MAP.USER_MANAGEMENT,
          type_metadata: JSON.stringify({
            uuid: user.uuid,
            updated_keys: updatedFields,
          }),
        });
        enqueueSuccessNotification('User updated successfully');

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListExternalUsers],
        });

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListAuditLogs],
        });

        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useGetManagementUser, user.uuid],
        });
      } else {
        enqueueErrorNotification('Error updating user');
      }
      return res;
    } catch (error) {
      enqueueErrorNotification((error as Error).message || 'Error updating user');
      return false;
    }
  };

  return {
    updateNewUser,
    isLoading: isPending,
    isError,
  };
};
